/**
 * @page PreiskalkulatorPDF
 * @route /calculator/pdf
 * @description Print-optimierte Seite für Preiskalkulator PDF-Export.
 *   Liest Kalkulationsdaten aus sessionStorage.pdfData.
 *   Auto-Print nach Render (via window.print()).
 */
import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

function formatPrice(value) {
  if (value === null || value === undefined) return '\u20AC0,00';
  const num = typeof value === 'string' ? parseFloat(value.replace(',', '.').replace('\u20AC', '')) : value;
  if (isNaN(num)) return '\u20AC0,00';
  return '\u20AC' + num.toFixed(2).replace('.', ',');
}

export default function PreiskalkulatorPDF() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('pdfData');
      if (raw) setData(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  // Auto-print after render
  useEffect(() => {
    if (data) {
      const timer = setTimeout(() => window.print(), 500);
      return () => clearTimeout(timer);
    }
  }, [data]);

  if (!data) {
    return (
      <div style={{
        fontFamily: "'DM Sans', sans-serif",
        maxWidth: 800, margin: '0 auto', padding: 80,
        textAlign: 'center', color: '#6B7280',
      }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1A1A2E', marginBottom: 8 }}>
          {t('calcPdf.noData')}
        </h2>
        <p style={{ fontSize: 14, marginBottom: 24 }}>{t('calcPdf.noDataDesc')}</p>
        <a
          href="/calculator"
          style={{
            display: 'inline-block', padding: '12px 28px',
            background: '#338edc', color: 'white', borderRadius: 8,
            fontSize: 14, fontWeight: 600, textDecoration: 'none',
          }}
        >
          {t('calcPdf.backToCalc')}
        </a>
      </div>
    );
  }

  const h = data.handling || {};
  const today = new Date().toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          .pdf-toolbar { display: none !important; }
          body { margin: 0; padding: 0; background: white; }
          @page { margin: 0; size: A4; }
        }
        @media screen {
          body { background: #F4F5FA; }
        }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", maxWidth: 800, margin: '0 auto', color: '#2D3748' }}>
        {/* Print toolbar (hidden in print) */}
        <div className="pdf-toolbar" style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: '24px 0' }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: '12px 28px', border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              background: '#338edc', color: 'white',
            }}
          >
            {t('calcPdf.print')}
          </button>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '12px 28px', borderRadius: 8,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              background: 'white', color: '#2D3748',
              border: '2px solid #E5E7EB',
            }}
          >
            {t('calcPdf.back')}
          </button>
        </div>

        <div style={{ background: 'white', padding: 48, borderRadius: 12 }}>
          {/* ── Header ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 28, borderBottom: '3px solid #338edc', marginBottom: 32 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>
              we<span style={{ color: '#338edc' }}>ship.</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E', letterSpacing: '-0.3px' }}>{t('calcPdf.docTitle')}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>{t('calcPdf.createdOn')} {today} &middot; {t('calcPdf.nonBinding')}</div>
            </div>
          </div>

          {/* ── Route Banner ── */}
          <div style={{
            background: '#1A1A2E', borderRadius: 10, padding: '20px 28px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 28, color: 'white',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.6, marginBottom: 4 }}>{t('calculator.shipFrom')}</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{data.fromFlag} {data.fromCountry || '\u2013'}</div>
            </div>
            <div style={{ fontSize: 20, opacity: 0.4 }}>&rarr;</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.6, marginBottom: 4 }}>{t('calculator.shipTo')}</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{data.toFlag} {data.toCountry || '\u2013'}</div>
            </div>
            <div style={{ fontSize: 20, opacity: 0.4 }}>&middot;</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
              }}>
                {data.carrierBadge || data.carrierCode || '\u2013'}
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.6, marginBottom: 4 }}>{t('calcPdf.carrier')}</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{data.carrierName || '\u2013'}</div>
              </div>
            </div>
          </div>

          {/* ── Details Grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
            <DetailCard label={t('calcPdf.volumeTier')} value={data.tierName || '\u2013'} sub={`${data.tierRange || ''} ${t('calcPdf.packagesMonth')}`} />
            <DetailCard label={t('calculator.weightPerPackage')} value={data.weight || '\u2013'} />
            <DetailCard label={t('calculator.skus')} value={data.skus !== undefined ? data.skus : '\u2013'} />
            <DetailCard label={t('calculator.picks')} value={data.picks !== undefined ? data.picks : '\u2013'} />
          </div>

          {/* ── Handling Fees ── */}
          <PriceSection title={t('calculator.handlingFees')}>
            <PriceRow label={t('calculator.orderProcessing')} detail="p.P." value={formatPrice(h.orderProcessing)} />
            <PriceRow label={t('calculator.picking')} detail={`${data.picks || 0} Picks`} value={formatPrice(h.picking)} />
            <PriceRow label={t('calculator.commission')} detail={`${data.skus || 0} SKUs`} value={formatPrice(h.commission)} />
            <PriceRow label={t('calculator.packagingFee')} detail={`${t('calcPdf.upTo')} ${data.weight || '\u2013'}`} value={formatPrice(h.packaging)} />
            <SubtotalRow label={t('calculator.handlingTotal')} value={formatPrice(h.total)} />
          </PriceSection>

          {/* ── Material Costs ── */}
          <PriceSection title={t('calculator.materialCosts')}>
            <PriceRow label={`${t('calcPdf.box')} ${data.materialName || ''}`} detail={data.materialDims ? `${data.materialDims} mm` : ''} value={formatPrice(data.material)} />
          </PriceSection>

          {/* ── Shipping Costs ── */}
          <PriceSection title={t('calculator.shippingCosts')}>
            <PriceRow label={t('calculator.porto')} detail={`${data.carrierBadge || data.carrierCode || ''} \u00B7 ${t('calcPdf.upTo')} ${data.weight || '\u2013'}`} value={formatPrice(data.porto)} />
          </PriceSection>

          {/* ── Extras ── */}
          {data.extras?.length > 0 && (
            <PriceSection title={t('calculator.extras')}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                {data.extras.map(e => (
                  <span key={e.code} style={{
                    padding: '6px 14px', background: '#EBF4FD', borderRadius: 20,
                    fontSize: 12, color: '#1557B0', fontWeight: 600,
                  }}>
                    {e.name} {e.price ? `(${formatPrice(e.price)})` : ''}
                  </span>
                ))}
              </div>
              {data.extrasTotal && (
                <SubtotalRow label={t('calculator.extrasTotal')} value={formatPrice(data.extrasTotal)} />
              )}
            </PriceSection>
          )}

          {/* ── Total ── */}
          <div style={{
            background: 'linear-gradient(135deg, #1A1A2E 0%, #253660 100%)',
            borderRadius: 12, padding: '24px 32px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 28,
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('calculator.total')}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                {t('calculator.perPackage')} &middot; {t('calcPdf.exclVat')}
              </div>
            </div>
            <div style={{ fontSize: 40, fontWeight: 800, color: 'white', fontFamily: "'JetBrains Mono', monospace", letterSpacing: -1 }}>
              <span style={{ fontSize: 22, opacity: 0.6, verticalAlign: 'super', marginRight: 2 }}>&euro;</span>
              {data.totalPerPackage ? data.totalPerPackage.toFixed(2).replace('.', ',') : '0,00'}
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.6 }}>
              We Ship Fulfillment GmbH<br />
              {t('calcPdf.allPricesExclVat')}<br />
              {t('calcPdf.nonBindingOffer')}
            </div>
            <div style={{ textAlign: 'right' }}>
              <a
                href={data.contactUrl || 'https://www.weship.eu/kontakt/'}
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-toolbar"
                style={{
                  display: 'inline-block', padding: '10px 24px',
                  background: '#338edc', color: 'white', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, textDecoration: 'none',
                }}
              >
                {t('calculator.contactExpert')}
              </a>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>www.weship.eu</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function DetailCard({ label, value, sub }) {
  return (
    <div style={{ background: '#F8F9FC', borderRadius: 8, padding: '14px 18px' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, color: '#9CA3AF', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1A1A2E', fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function PriceSection({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#338edc',
        textTransform: 'uppercase', letterSpacing: 1.2,
        paddingBottom: 8, borderBottom: '2px solid #338edc', marginBottom: 0,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function PriceRow({ label, detail, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderBottom: '1px solid #F3F4F6',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: '#2D3748' }}>{label}</span>
        {detail && (
          <span style={{
            fontSize: 11, color: '#1557B0', background: '#EBF4FD',
            padding: '2px 8px', borderRadius: 4,
            fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
          }}>
            {detail}
          </span>
        )}
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: '#1A1A2E', fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}

function SubtotalRow({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0 4px', marginTop: 4, borderTop: '2px dashed #E5E7EB',
    }}>
      <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#2D3748' }}>{label}</span>
      <span style={{ fontSize: 16, fontWeight: 800, color: '#338edc', fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}
